import React from 'react';
import { format } from 'date-fns';
import {
  RequestQuote,
  AttachMoney,
  CalendarMonth,
  Business,
  AttachFile,
  GetApp,
} from '@mui/icons-material';
import {
  BudgetRequest,
  BudgetRequestStatus,
  BudgetRequestRecipientStatus,
  BudgetRequestRecipient,
} from '@models/budget.model';
import { useTranslation } from '@hooks/useTranslation';

interface BudgetRequestCardProps {
  budgetRequest: BudgetRequest;
  activeSubTab: 'received' | 'delegated';
  isDelegatedTask: boolean;
  isCompletedCancelledBudget: (request: BudgetRequest) => boolean;
  title?: string;
}

export const BudgetRequestCard: React.FC<BudgetRequestCardProps> = ({
  budgetRequest,
  activeSubTab,
  isDelegatedTask,
  isCompletedCancelledBudget,
  title,
}) => {
  const tTaskDetail = useTranslation('taskDetail');
  // Sprint 28 change request (global sweep, defect fix). `recipient.status` below used to
  // render the raw `BudgetRequestRecipientStatus` enum.
  const recipientStatusTextMap: Record<string, string> = {
    [BudgetRequestRecipientStatus.SENT]: tTaskDetail.budget.recipientStatusSent,
    [BudgetRequestRecipientStatus.VIEWED]: tTaskDetail.budget.recipientStatusViewed,
    [BudgetRequestRecipientStatus.RESPONDED]: tTaskDetail.budget.recipientStatusResponded,
    [BudgetRequestRecipientStatus.ACCEPTED]: tTaskDetail.budget.recipientStatusAccepted,
    [BudgetRequestRecipientStatus.REJECTED]: tTaskDetail.budget.recipientStatusRejected,
  };

  const formatCurrency = (amount?: number): string => {
    if (!amount) return tTaskDetail.budget.notSpecified;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date?: string | Date) => {
    if (!date) return '-';
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const acceptedRecipient = budgetRequest.recipients?.find(
    (recipient: BudgetRequestRecipient) =>
      recipient.status === BudgetRequestRecipientStatus.ACCEPTED,
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {title && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                isCompletedCancelledBudget(budgetRequest)
                  ? 'bg-orange-100 text-orange-800'
                  : budgetRequest.status === BudgetRequestStatus.COMPLETED
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800'
              }`}>
              {isCompletedCancelledBudget(budgetRequest)
                ? tTaskDetail.budget.statusCompletedCancelled
                : budgetRequest.status}
            </span>
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tTaskDetail.budget.amountLabel}
            </label>
            <div className="flex items-center gap-2">
              <AttachMoney className="h-4 w-4 text-gray-500" />
              <span className="text-lg font-semibold text-green-600">
                {formatCurrency(budgetRequest.maxBudgetAmount)}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tTaskDetail.budget.startDateLabel}
            </label>
            <div className="flex items-center gap-2">
              <CalendarMonth className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-900">
                {formatDate(budgetRequest.requestedStartDate)}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tTaskDetail.budget.endDateLabel}
            </label>
            <div className="flex items-center gap-2">
              <CalendarMonth className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-900">
                {formatDate(budgetRequest.requestedEndDate)}
              </span>
            </div>
          </div>

          {acceptedRecipient?.respondedAt && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {activeSubTab === 'delegated'
                  ? !isDelegatedTask
                    ? tTaskDetail.budget.delegationDate
                    : tTaskDetail.budget.subDelegationDate
                  : tTaskDetail.budget.acceptedDate}
              </label>
              <div className="flex items-center gap-2">
                <CalendarMonth className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-900">
                  {formatDate(acceptedRecipient.respondedAt)}
                </span>
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {activeSubTab === 'delegated'
                ? acceptedRecipient
                  ? tTaskDetail.budget.assignedOrganization
                  : tTaskDetail.budget.budgetRecipients
                : tTaskDetail.budget.originatorOrganization}
            </label>
            <div className="flex items-start gap-2">
              <Business className="h-4 w-4 text-gray-500 mt-0.5" />
              <div className="text-sm text-gray-900">
                {activeSubTab === 'delegated'
                  ? acceptedRecipient
                    ? acceptedRecipient.recipientOrganization?.name
                    : budgetRequest.recipients && budgetRequest.recipients.length > 0
                      ? (
                        <div className="space-y-1">
                          {budgetRequest.recipients.map((recipient: BudgetRequestRecipient) => (
                            <div key={recipient.recipientOrganizationId} className="flex items-center gap-2">
                              <span>{recipient.recipientOrganization?.name || tTaskDetail.budget.unknown}</span>
                              <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${
                                recipient.status === BudgetRequestRecipientStatus.ACCEPTED
                                  ? 'bg-green-100 text-green-800'
                                  : recipient.status === BudgetRequestRecipientStatus.REJECTED
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {recipientStatusTextMap[recipient.status] || recipient.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      )
                      : tTaskDetail.budget.notAvailable
                  : budgetRequest.originatorOrganization?.name || tTaskDetail.budget.notAvailable}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {budgetRequest.description && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tTaskDetail.budget.description}
            </label>
            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
              {budgetRequest.description}
            </p>
          </div>
        )}

        {/* Documents */}
        {(budgetRequest.documents?.length ?? 0) > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tTaskDetail.budget.attachedDocuments}
            </label>
            <div className="space-y-2">
              {budgetRequest.documents?.map((doc, index) => (
                <div
                  key={`request-${index}`}
                  className="flex items-center gap-3 p-2 bg-green-50 rounded border border-green-200 cursor-pointer hover:bg-green-100"
                  onClick={() => doc.url && window.open(doc.url, '_blank')}>
                  <AttachFile className="h-4 w-4 text-green-600" />
                  <span className="flex-1 text-sm text-gray-700">
                    {doc.name}
                  </span>
                  <span className="text-xs text-green-600">
                    {activeSubTab === 'delegated'
                      ? !isDelegatedTask
                        ? tTaskDetail.budget.delegationLabel
                        : tTaskDetail.budget.subDelegationLabel
                      : tTaskDetail.budget.budgetRequestLabel}
                  </span>
                  <GetApp className="h-4 w-4 text-green-600" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              window.open(
                `/budgets?budgetRequestId=${budgetRequest.budgetRequestId}`,
                '_blank',
              );
            }}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50">
            <RequestQuote className="h-3 w-3 mr-1" />
            {tTaskDetail.budget.viewDetails}
          </button>
        </div>
      </div>
    </div>
  );
};